/**
 * Faixa de bolinhas — arraste horizontal com translate (sempre funciona com overflow).
 */
(function () {
  const ROW = '[data-vestfirma-swatches-row]';
  const TRACK_SEL = '[data-swatches-track], .vestfirma-card-swatches__list, .video-testimonials__product-swatches';
  const DRAG_THRESHOLD = 6;
  const CARD_LINK_SEL =
    '[data-swatch-product-link], .vestfirma-collection-card-gallery__link, .home-product-grid__media-link, a.ml-card-info, a.product-card__link';

  /** @type {WeakMap<HTMLElement, () => void>} */
  const rowCleanups = new WeakMap();

  function isTouchSwatchRow() {
    return window.matchMedia('(hover: none), (max-width: 749px)').matches;
  }

  /**
   * @param {ParentNode | null | undefined} productRoot
   * @param {string} productUrl
   */
  function updateCardProductLinks(productRoot, productUrl) {
    if (!productRoot || !productUrl) return;
    productRoot.querySelectorAll(CARD_LINK_SEL).forEach((node) => {
      if (node instanceof HTMLAnchorElement) node.href = productUrl;
    });
  }

  function variantStorageKey(productId) {
    return `vestfirma-card-variant:${productId}`;
  }

  /**
   * @param {string | undefined} productId
   * @param {string | undefined} variantId
   */
  function rememberCardVariant(productId, variantId) {
    if (!productId || !variantId) return;
    try {
      sessionStorage.setItem(variantStorageKey(productId), variantId);
    } catch (_) {
      /* private mode */
    }
  }

  /**
   * @param {string | undefined} productId
   * @returns {string | null}
   */
  function recallCardVariant(productId) {
    if (!productId) return null;
    try {
      return sessionStorage.getItem(variantStorageKey(productId));
    } catch (_) {
      return null;
    }
  }

  /**
   * @param {HTMLElement} row
   * @param {HTMLElement[]} swatches
   * @param {ParentNode | null | undefined} productRoot
   * @returns {HTMLElement | undefined}
   */
  function resolveActiveSwatch(row, swatches, productRoot) {
    const productId = productRoot instanceof HTMLElement ? productRoot.dataset.productId : undefined;
    const storedId = recallCardVariant(productId);

    if (storedId) {
      const fromStore = swatches.find((s) => {
        const input = s.querySelector('input[type="radio"]');
        return input instanceof HTMLInputElement && input.dataset.variantId === storedId;
      });
      if (fromStore) return fromStore;
    }

    const fromDom = swatches.find((s) => s.querySelector('input[type="radio"]:checked'));
    if (fromDom) return fromDom;

    return swatches.find((s) => s.classList.contains('is-selected')) || swatches[0];
  }

  /**
   * @param {HTMLElement} viewport
   */
  function createScroller(viewport) {
    const track = viewport.querySelector(TRACK_SEL);
    if (!(track instanceof HTMLElement)) return null;

    const getMax = () => Math.max(0, track.scrollWidth - viewport.clientWidth);

    const getPos = () => {
      const stored = parseFloat(viewport.dataset.scrollPos || '0');
      return Number.isFinite(stored) ? stored : 0;
    };

    /** @param {number} left */
    const setPos = (left) => {
      const max = getMax();
      const x = Math.max(0, Math.min(max, left));
      track.style.transform = `translate3d(${-x}px, 0, 0)`;
      viewport.dataset.scrollPos = String(x);
      viewport.scrollLeft = x;
      return x;
    };

    const scrollChipIntoView = (chip) => {
      if (!(chip instanceof HTMLElement)) return;
      const max = getMax();
      if (max <= 0) return;

      const vpRect = viewport.getBoundingClientRect();
      const chipRect = chip.getBoundingClientRect();
      let pos = getPos();

      if (chipRect.left < vpRect.left + 4) {
        pos -= vpRect.left + 4 - chipRect.left;
      } else if (chipRect.right > vpRect.right - 4) {
        pos += chipRect.right - (vpRect.right - 4);
      }

      setPos(pos);
    };

    return { track, getMax, getPos, setPos, scrollChipIntoView };
  }

  /**
   * @param {HTMLElement} row
   */
  function destroyRow(row) {
    const cleanup = rowCleanups.get(row);
    if (cleanup) {
      cleanup();
      rowCleanups.delete(row);
    }
    delete row.dataset.vestfirmaSwatchesInit;
    delete row.dataset.vestfirmaSwatchDrag;
    delete row.dataset.vestfirmaSwatchMoved;
  }

  /**
   * @param {HTMLElement} row
   * @param {HTMLElement} el
   * @param {HTMLElement[]} swatches
   * @param {ParentNode | null | undefined} productRoot
   * @param {HTMLImageElement | null | undefined} img
   * @param {ReturnType<typeof createScroller>} scroller
   * @param {boolean} scrollIntoView
   * @param {boolean} silent — não dispara change (evita loop na init)
   */
  function applySwatch(
    row,
    el,
    swatches,
    productRoot,
    img,
    scroller,
    scrollIntoView,
    silent
  ) {
    if (!el || el.hasAttribute('disabled')) return;

    const chip = el.matches('[data-swatch]') ? el : el.closest('[data-swatch]');
    if (!(chip instanceof HTMLElement)) return;

    const input = chip.querySelector('input[type="radio"]');
    let productUrl = chip.dataset.productUrl || '';

    if (input instanceof HTMLInputElement) {
      if (!input.checked) {
        input.checked = true;
      }
      if (!silent) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (!productUrl && input.dataset.variantId) {
        const base = row.closest('swatches-variant-picker-component')?.dataset.productUrl?.split('?')[0];
        if (base) {
          const url = new URL(base, window.location.origin);
          url.searchParams.set('variant', input.dataset.variantId);
          productUrl = url.pathname + url.search;
        }
      }
    } else {
      swatches.forEach((s) => {
        const on = s === chip;
        s.classList.toggle('is-selected', on);
        s.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    const previewUrl = chip.dataset.previewUrl || input?.dataset.previewUrl;
    if (previewUrl && img instanceof HTMLImageElement) {
      img.src = previewUrl;
      img.removeAttribute('srcset');
    }

    if (productUrl) {
      updateCardProductLinks(productRoot, productUrl);
    }

    const productId = productRoot instanceof HTMLElement ? productRoot.dataset.productId : undefined;
    const variantId = input instanceof HTMLInputElement ? input.dataset.variantId : undefined;
    rememberCardVariant(productId, variantId);

    if (scrollIntoView) {
      requestAnimationFrame(() => scroller.scrollChipIntoView(chip));
    }
  }

  /**
   * @param {HTMLElement} row
   */
  function initRow(row) {
    if (row.dataset.vestfirmaSwatchesInit === '1') return;

    const viewport = row.querySelector('[data-swatches-viewport]');
    if (!(viewport instanceof HTMLElement)) return;

    const scroller = createScroller(viewport);
    if (!scroller) return;

    const swatches = /** @type {HTMLElement[]} */ ([
      ...row.querySelectorAll('[data-swatch]'),
    ]);
    if (!swatches.length) return;

    destroyRow(row);

    const productRoot =
      row.closest('[data-vestfirma-product-card]') ||
      row.closest('product-card') ||
      row.closest('.video-testimonials__product');

    const img = productRoot?.querySelector(
      '.video-testimonials__product-img, .home-product-grid__img, .card-gallery img'
    );

  /**
   * @param {HTMLElement} el
   * @param {boolean} [scrollIntoView]
   * @param {boolean} [silent]
   */
    const selectSwatch = (el, scrollIntoView = true, silent = false) => {
      applySwatch(row, el, swatches, productRoot, img, scroller, scrollIntoView, silent);
    };

    const hasOverflow = () => scroller.getMax() > 2 || swatches.length > 4;

    const controller = new AbortController();
    const { signal } = controller;

    swatches.forEach((swatch) => {
      const input = swatch.querySelector('input[type="radio"]');
      if (input) {
        input.addEventListener(
          'change',
          () => {
            if (input.checked) selectSwatch(swatch, true, true);
          },
          { signal }
        );
      }

      swatch.addEventListener(
        'click',
        (e) => {
          if (row.dataset.vestfirmaSwatchMoved === '1') {
            e.preventDefault();
            e.stopPropagation();
            row.dataset.vestfirmaSwatchMoved = '0';
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          selectSwatch(swatch, true, false);
        },
        { signal, capture: true }
      );
    });

    if (isTouchSwatchRow()) {
      bindDrag(row, viewport, scroller, swatches, selectSwatch, hasOverflow, signal);
    }

    let relayoutPending = false;
    const relayout = () => {
      if (relayoutPending) return;
      relayoutPending = true;
      requestAnimationFrame(() => {
        relayoutPending = false;
        scroller.setPos(scroller.getPos());
      });
    };

    window.addEventListener('resize', relayout, { passive: true, signal });

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(relayout);
      ro.observe(viewport);
      signal.addEventListener('abort', () => ro.disconnect());
    }

    rowCleanups.set(row, () => controller.abort());

    const initial = resolveActiveSwatch(row, swatches, productRoot);
    if (initial) selectSwatch(initial, false, true);

    row.dataset.vestfirmaSwatchesInit = '1';
  }

  /**
   * @param {HTMLElement} row
   * @param {HTMLElement} viewport
   * @param {ReturnType<typeof createScroller>} scroller
   * @param {HTMLElement[]} swatches
   * @param {(el: HTMLElement, scrollIntoView?: boolean, silent?: boolean) => void} selectSwatch
   * @param {() => boolean} hasOverflow
   * @param {AbortSignal} signal
   */
  function bindDrag(row, viewport, scroller, swatches, selectSwatch, hasOverflow, signal) {
    if (row.dataset.vestfirmaSwatchDrag === '1') return;
    row.dataset.vestfirmaSwatchDrag = '1';

    let active = false;
    let moved = false;
    let startX = 0;
    let startPos = 0;
    /** @type {number | null} */
    let pointerId = null;

    const finish = () => {
      if (!active) return;
      active = false;
      pointerId = null;
      viewport.classList.remove('is-dragging');
      window.setTimeout(() => {
        row.dataset.vestfirmaSwatchMoved = '0';
      }, 100);
    };

    const applyMove = (clientX) => {
      const dx = clientX - startX;
      if (Math.abs(dx) < DRAG_THRESHOLD) return;

      moved = true;
      row.dataset.vestfirmaSwatchMoved = '1';

      if (hasOverflow()) {
        scroller.setPos(startPos - dx);
      }
    };

    const start = (clientX, id) => {
      active = true;
      moved = false;
      row.dataset.vestfirmaSwatchMoved = '0';
      pointerId = id;
      startX = clientX;
      startPos = scroller.getPos();
      viewport.classList.add('is-dragging');
    };

    const onEnd = () => {
      if (!active) return;
      if (moved && hasOverflow()) {
        const vp = viewport.getBoundingClientRect();
        const center = vp.left + vp.width / 2;
        let best = swatches[0];
        let bestDist = Infinity;
        swatches.forEach((btn) => {
          const r = btn.getBoundingClientRect();
          const d = Math.abs(r.left + r.width / 2 - center);
          if (d < bestDist) {
            bestDist = d;
            best = btn;
          }
        });
        selectSwatch(best, true, false);
      }
      finish();
    };

    const bind = (el) => {
      el.addEventListener(
        'touchstart',
        (e) => {
          if (e.touches.length !== 1) return;
          e.stopPropagation();
          start(e.touches[0].clientX, e.touches[0].identifier);
        },
        { passive: true, capture: true, signal }
      );

      el.addEventListener(
        'touchmove',
        (e) => {
          if (!active) return;
          const t = e.touches[0];
          if (!t) return;
          if (hasOverflow() && Math.abs(t.clientX - startX) > DRAG_THRESHOLD) {
            e.preventDefault();
            e.stopPropagation();
          }
          applyMove(t.clientX);
        },
        { passive: false, capture: true, signal }
      );

      el.addEventListener('touchend', (e) => { e.stopPropagation(); onEnd(); }, { capture: true, signal });
      el.addEventListener('touchcancel', onEnd, { capture: true, signal });

      el.addEventListener(
        'pointerdown',
        (e) => {
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          e.stopPropagation();
          start(e.clientX, e.pointerId);
          try {
            viewport.setPointerCapture(e.pointerId);
          } catch (_) {
            /* ignore */
          }
        },
        { capture: true, signal }
      );

      el.addEventListener(
        'pointermove',
        (e) => {
          if (!active || e.pointerId !== pointerId) return;
          if (hasOverflow() && Math.abs(e.clientX - startX) > DRAG_THRESHOLD) {
            e.preventDefault();
            e.stopPropagation();
          }
          applyMove(e.clientX);
        },
        { capture: true, signal }
      );

      el.addEventListener(
        'pointerup',
        (e) => {
          if (!active || e.pointerId !== pointerId) return;
          e.stopPropagation();
          onEnd();
        },
        { capture: true, signal }
      );

      el.addEventListener(
        'pointercancel',
        (e) => {
          if (!active || e.pointerId !== pointerId) return;
          onEnd();
        },
        { capture: true, signal }
      );
    };

    bind(row);
    bind(viewport);
    viewport.addEventListener('lostpointercapture', finish, { signal });
  }

  /**
   * Reaplica foto + links a partir da bolinha ativa (Voltar / bfcache).
   * @param {HTMLElement} row
   */
  function resyncRow(row) {
    const viewport = row.querySelector('[data-swatches-viewport]');
    if (!(viewport instanceof HTMLElement)) return;

    const swatches = /** @type {HTMLElement[]} */ ([...row.querySelectorAll('[data-swatch]')]);
    if (!swatches.length) return;

    const productRoot =
      row.closest('[data-vestfirma-product-card]') ||
      row.closest('product-card') ||
      row.closest('.video-testimonials__product');

    const active = resolveActiveSwatch(row, swatches, productRoot);
    if (!active) return;

    const img = productRoot?.querySelector(
      '.video-testimonials__product-img, .home-product-grid__img, .card-gallery img'
    );
    const chip = active.matches('[data-swatch]') ? active : active.closest('[data-swatch]');
    if (!(chip instanceof HTMLElement)) return;

    const input = chip.querySelector('input[type="radio"]');
    let productUrl = chip.dataset.productUrl || '';

    if (input instanceof HTMLInputElement) {
      input.checked = true;
      if (!productUrl && input.dataset.variantId) {
        const base = row.closest('swatches-variant-picker-component')?.dataset.productUrl?.split('?')[0];
        if (base) {
          const url = new URL(base, window.location.origin);
          url.searchParams.set('variant', input.dataset.variantId);
          productUrl = url.pathname + url.search;
        }
      }
      rememberCardVariant(
        productRoot instanceof HTMLElement ? productRoot.dataset.productId : undefined,
        input.dataset.variantId
      );
    }

    const previewUrl = chip.dataset.previewUrl || input?.dataset.previewUrl;
    if (previewUrl && img instanceof HTMLImageElement) {
      img.src = previewUrl;
      img.removeAttribute('srcset');
    }

    if (productUrl) {
      updateCardProductLinks(productRoot, productUrl);
    }
  }

  /**
   * @param {ParentNode} [root]
   */
  function resyncAll(root = document) {
    const scope = root instanceof Document ? root : root;
    scope.querySelectorAll(ROW).forEach((row) => {
      if (row instanceof HTMLElement && row.dataset.vestfirmaSwatchesInit === '1') {
        resyncRow(row);
      }
    });
  }

  /**
   * Corrige DOM após Voltar (bfcache / view transition): badge na foto.
   * Não reinicializa listeners — evita acúmulo e travamento.
   * @param {ParentNode} [root]
   */
  function restoreCardLayout(root = document) {
    const scope = root instanceof Document ? root : root;

    scope.querySelectorAll('[data-vestfirma-product-card], product-card.vestfirma-product-card, product-card.home-product-grid__card').forEach((card) => {
      if (!(card instanceof HTMLElement)) return;

      card.querySelectorAll('[data-view-transition-type], [data-view-transition-triggered]').forEach((el) => {
        el.removeAttribute('data-view-transition-type');
        el.removeAttribute('data-view-transition-triggered');
      });
      card.style.removeProperty('view-transition-name');

      const badge = card.querySelector('.vestfirma-card-badge--image');
      const figure = card.querySelector('.vestfirma-collection-card-gallery__figure, .home-product-grid__figure');
      const media = card.querySelector('.vestfirma-collection-card-gallery__media, .home-product-grid__media, .card-gallery');

      if (badge instanceof HTMLElement) {
        const target = figure || media;
        if (target && badge.parentElement !== target) {
          target.appendChild(badge);
        }
      }
    });
  }

  /**
   * @param {ParentNode} [root]
   */
  function initAll(root = document) {
    const scope = root instanceof Document ? root : root;
    scope.querySelectorAll(ROW).forEach((row) => {
      if (row instanceof HTMLElement) initRow(row);
    });
  }

  let initScheduled = false;

  const scheduleInit = () => {
    if (initScheduled) return;
    initScheduled = true;

    const run = () => {
      initAll();
      resyncAll();
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 600 });
    } else {
      requestAnimationFrame(run);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInit, { once: true });
  } else {
    scheduleInit();
  }

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target instanceof HTMLElement) initAll(e.target);
  });

  window.addEventListener('pageshow', (event) => {
    restoreCardLayout();
    resyncAll();
    if (event.persisted) {
      requestAnimationFrame(() => {
        restoreCardLayout();
        resyncAll();
      });
    }
  });

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const link = target.closest(CARD_LINK_SEL);
      if (!(link instanceof HTMLAnchorElement)) return;
      const productRoot =
        link.closest('[data-vestfirma-product-card]') ||
        link.closest('product-card') ||
        link.closest('.video-testimonials__product');
      const input = productRoot?.querySelector('input[type="radio"][name*="-card-swatch"]:checked');
      if (input instanceof HTMLInputElement) {
        const productId =
          productRoot instanceof HTMLElement ? productRoot.dataset.productId : undefined;
        rememberCardVariant(productId, input.dataset.variantId);
      }
    },
    true
  );

  window.VestfirmaSwatchesRow = { initAll, initRow, resyncAll, resyncRow, restoreCardLayout, destroyRow };
})();
